import { Link } from "@inertiajs/react"
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  Coins,
  Flame,
  Lock,
  Sparkles,
  TrendingUp,
} from "lucide-react"
import { type ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { formatXpNumber } from "@/lib/format-xp"
import { toAppLocale } from "@/lib/i18n/locales"
import { COVER_ASPECT_CLASS } from "@/lib/mission-cover"
import { composeRewardAmounts, rewardAtom } from "@/lib/mission-reward-amounts"
import { MISSION_TYPE_CHIPS } from "@/lib/mission-type-chips"
import { cn } from "@/lib/utils"
import { creatorMissionDetailPath } from "@/routes"
import type { Locale } from "@/types"
import type { Collab, CollabUrgency } from "@/types/collab"

import { ActionRequiredBadge } from "./action-required-badge"
import { GiftLine } from "./gift-line"
import { RecencyLine } from "./recency-line"
import { SocialProofCluster } from "./social-proof-cluster"
import { type MissionCardStatus, StatusChip } from "./status-chip"
import { UrgencyChip } from "./urgency-chip"

/**
 * OKS-480 — canonical CTA treatment, replacing the OKS-472 variant+opacity
 * hacks. Mirrors the shipped `CTAButton` semantics:
 *   - `enabled`           → filled primary, actionable.
 *   - `disabled-progress` → muted read-only affordance (focusable, no handler).
 *   - `destructive`       → destructive intent (e.g. `flagged` Resubmit).
 *   - `outline`           → secondary outline (e.g. `issue` / `expired`).
 */
export type MissionCardCtaState =
  | "enabled"
  | "disabled-progress"
  | "destructive"
  | "outline"

/**
 * OKS-472 — MissionCard (Figma `164:695`). The brand-less, cover-first card
 * the design system was missing — distinct from `CollabCard` (brand row +
 * filled-CTA submission flow). Reused on the §05 Crew Home, S2-04 (OKS-468),
 * the crew feed, and mission drops.
 *
 * Anatomy (top → bottom):
 *   - Cover image (16:9 slot) with OVERLAID urgency chip (top-left) +
 *     OVERLAID social-proof cluster (bottom-left). Collapses cleanly when no
 *     cover (chip + social proof then render inline in the content header).
 *   - Title (clamp-2)
 *   - Urgency-coded meta line (icon + colour + text)
 *   - Action region (see below)
 *
 * ## Action modes (ticket §"Action props")
 *
 *   - **List mode (default — no `filledCTA`):** the WHOLE card is a single
 *     labelled link to its §08 detail (stretched-link overlay) + a decorative
 *     chevron. NO filled CTA (binding rule — UX Spec §2.5 / PRD §7.1).
 *   - **Focused mode (`filledCTA` provided):** the card is NOT a whole-card
 *     link; a filled primary CTA is the action. Reserved for §08 / S2-04.
 *   - **`secondaryAction`:** an optional ghost button (e.g. overdue "Post
 *     now"), a distinct labelled control layered above the stretched link.
 *
 * Brand isolation: renders only fields already on the brand-scoped `collab`
 * wire; no cross-brand data, social proof is first-name + avatar only.
 */
interface MissionCardLink {
  href: string
  labelKey: string
}

/**
 * Ghost secondary action (e.g. overdue "Post now"). Either navigates
 * (`href`) or fires an in-page handler (`onClick` — e.g. opening the inline
 * submission picker). `onClick` wins when both are set.
 */
interface MissionCardAction {
  labelKey: string
  href?: string
  onClick?: () => void
}

/**
 * OKS-480 — wiring for the canonical in-card CTA (`ctaState` + `ctaLabel`).
 * Capacity-only this ticket: the §05 call site keeps Option B's welded
 * CTA-less model (TRD KD-4), so this is exercised via stories/Vitest. `onClick`
 * wins when both are set; omit both for a `disabled-progress` affordance.
 */
interface MissionCardCtaAction {
  href?: string
  onClick?: () => void
}

interface MissionCardProps {
  collab: Collab
  /** Whole-card link target (list mode). Defaults to the §08 detail. */
  href?: string
  /** Show the decorative tap-through chevron (list mode). Default `true`. */
  showChevron?: boolean
  /** Ghost secondary action — time-critical/overdue only (e.g. "Post now"). */
  secondaryAction?: MissionCardAction
  /**
   * Filled primary CTA — reserved for focused/detail contexts (§08, S2-04).
   * When provided, the card is NOT a whole-card link and the chevron is
   * dropped. MUST NOT be used in list mode (UX Spec §2.5).
   */
  filledCTA?: MissionCardLink
  /**
   * Optional overflow control rendered in the card's top-right corner above
   * the stretched link (e.g. the Open-`assigned` Decline menu). Not a primary
   * CTA — a secondary affordance that stays reachable on the browse card.
   */
  overflow?: ReactNode
  /**
   * OKS-477 — Locked teaser mode for §07 mission drops. When `true` the card
   * is a NON-interactive teaser: the cover is dimmed under a centered lock
   * badge + "Locked until drop" caption, and the whole-card link, chevron,
   * filledCTA, and secondaryAction are ALL suppressed. The card is
   * `aria-disabled` and removed from the tab order. Defaults to `false` — no
   * existing call-site passes it, so list/focused behaviour is unchanged.
   *
   * Distinct from OKS-480's `dimmed`/`expired` treatment (which KEEPS the chip
   * + CTA): `locked` is the strictest, chip-less, CTA-less teaser (TRD KD-3).
   *
   * OKS-1002 — see `lockedActionable` for the single, opt-in exception.
   */
  locked?: boolean
  /**
   * OKS-1002 (D3) — a `locked` teaser the creator can still ACT on: it keeps
   * the lock treatment but regains the status chip and the whole-card link.
   * Only meaningful alongside `locked`. **Defaults to `false`**, so every
   * existing caller (`MissionDrop`, `WelcomeBack`) is byte-identical and the
   * OKS-477 non-interactive-teaser contract is untouched for them.
   *
   * ⚠️ A PROP, not a flag this component reads off the collab. Teaser
   * actionability is a CALL-SITE POLICY, exactly like `locked` itself —
   * `MissionCard` deliberately never touches `collab.address_action_required`,
   * so the flag→status derivation stays in `missionCardStatusFor` alone. Two
   * components deriving one signal is how they drift apart.
   *
   * ⚠️ It does NOT unlock the teaser's other suppressions. The dimmed cover,
   * lock badge, "Locked until drop" caption, type chip, gift line and rewards
   * all hang off `locked` and all stay hidden. The lock withholds the
   * mission's content; this withholds nothing about the mission, only about
   * the creator's own outstanding task.
   */
  lockedActionable?: boolean
  /**
   * OKS-480 — status driver ("one prop = one status"). Presets the top-left
   * StatusChip text/tone/icon. When set on a calm row the StatusChip REPLACES
   * the top-left UrgencyChip (UX Spec §1/§6); `overdue` rows keep the
   * UrgencyChip overlay. Absent → legacy UrgencyChip path (back-compat).
   *
   * OKS-1002 (D1) — `"to_ship_address_pending"` is the ONE status that takes
   * the slot even on an `overdue` row. See `useStatusChip` for why, and why
   * the exception is a literal rather than a predicate.
   */
  status?: MissionCardStatus
  /**
   * OKS-480 — derived-urgency overlay flag (NOT a collab state). When `true`
   * the top-left keeps the `UrgencyChip` (urgency persists for overdue rows)
   * even if `status` is set, and the card stays submittable. Defaults `false`.
   *
   * OKS-1002 (D1) — one status now outranks this: see `status` above. Urgency
   * itself is untouched everywhere else on an overdue row (section, red meta
   * line, colour, rewards rule) — only which chip wins the slot changed.
   */
  overdue?: boolean
  /**
   * OKS-480 — canonical CTA treatment (replaces variant+opacity hacks). When
   * set, an in-card CTA renders (the card is NOT a whole-card link). Capacity-
   * only at the §05 call site (TRD KD-4). Pair with `ctaLabel` + `ctaAction`.
   */
  ctaState?: MissionCardCtaState
  /** OKS-480 — i18n key for the in-card CTA label (e.g. `collab.cta.label_key`). */
  ctaLabel?: string
  /** OKS-480 — in-card CTA wiring (href/onClick). Omit for disabled-progress. */
  ctaAction?: MissionCardCtaAction
  /**
   * OKS-480 — success treatment for an approved/`completed` collab: a green
   * check (top-right) + success tint. SUPPRESSES the in-card CTA (UX Spec §3
   * row 12). Capacity-only on §05 (completed leaves the active list).
   */
  approved?: boolean
  /**
   * OKS-480 — dimmed treatment for the `expired` state: a muted scrim over
   * cover + content while KEEPING the StatusChip + the outline "Request
   * reopen" CTA legible (UX Spec §3 row 14). `status="expired"` auto-implies
   * it. Distinct from `locked` (the chip-less drop teaser). Capacity-only —
   * no expiry data/backend today (lifecycle §5).
   */
  dimmed?: boolean
  /*
   * OKS-992 — the `tracking` prop is GONE, with its `TrackingNumberLine`
   * render. `tracking_number` no longer rides the list wire at all.
   *
   * Two defects went with it. A creator whose three items shipped in ONE
   * parcel saw three identical references and read them as three parcels; and
   * a URL-only shipment — the COMMON case since OKS-984's queue asks for a
   * carrier LINK and derives a number only for recognised carriers — rendered
   * nothing here at all, because this prop was number-only.
   *
   * The reference now lives on the mission detail beside the carrier link that
   * makes it actionable. The whole card is already a labelled link there, so
   * it is one tap away and never duplicated.
   */
  /**
   * OKS-637 — when `true`, renders the amber "Action required" badge in the
   * title row. Driven by the page's `ready_to_submit_ids` set (the same signal
   * behind the Missions-tab count). Never shown on a locked teaser (locked
   * collabs are never ready-to-submit). Defaults `false`.
   */
  actionRequired?: boolean
  className?: string
}

/** Urgency-coded meta line config — icon + colour paired with the text so the
 *  cue is never colour-only (a11y). Keyed by the server `CollabUrgency`. */
const META_VARIANTS: Record<
  CollabUrgency,
  { icon: typeof Calendar; textClass: string; i18nKey: string }
> = {
  default: {
    icon: Calendar,
    textClass: "text-muted-foreground",
    i18nKey: "creator_home.deadline",
  },
  due_today: {
    icon: Flame,
    textClass: "text-amber-600",
    i18nKey: "creator_home.urgency.due_today",
  },
  overdue: {
    icon: AlertTriangle,
    textClass: "text-destructive",
    i18nKey: "creator_home.urgency.overdue",
  },
}

/** Format a `YYYY-MM-DD` deadline into a short locale date, or "" when absent
 *  / malformed (the meta key then renders without a date). */
function formatDeadline(deadlineAt: string | null, locale: string): string {
  if (!deadlineAt || !/^\d{4}-\d{2}-\d{2}$/.test(deadlineAt)) return ""
  const date = new Date(`${deadlineAt}T12:00:00`)
  if (Number.isNaN(date.getTime())) return ""
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
  }).format(date)
}

export function MissionCard({
  collab,
  href,
  showChevron = true,
  secondaryAction,
  filledCTA,
  overflow,
  locked = false,
  lockedActionable = false,
  status,
  overdue = false,
  ctaState,
  ctaLabel,
  ctaAction,
  approved = false,
  dimmed = false,
  actionRequired = false,
  className,
}: MissionCardProps) {
  const { t, i18n } = useTranslation()
  const { mission } = collab
  const [coverErrored, setCoverErrored] = useState(false)
  // OKS-452 — Cover-image fade-in. Independent of `coverErrored` (load
  // success vs. load failure). `motion-safe:` gates the transition so
  // reduced-motion users see the image at full opacity as soon as it loads.
  const [coverLoaded, setCoverLoaded] = useState(false)

  const showCover = Boolean(mission.cover_image_url) && !coverErrored
  const meta = META_VARIANTS[mission.urgency]
  const MetaIcon = meta.icon
  const locale: Locale = toAppLocale(i18n.language)
  const deadlineText = formatDeadline(mission.deadline_at, i18n.language)
  const metaText = t(meta.i18nKey, { date: deadlineText })
  // ─── OKS-938 F2 — no deadline ⇒ no deadline segment ───
  //
  // `creator_home.deadline` is "Due %{date}" — the line exists only to state a
  // date. With none it interpolated to a bare "Deadline " that contradicted the
  // "no deadline to miss" bonus line two rows below. Drop the WHOLE segment
  // (icon, word, and the separator that would dangle after it), not the date.
  //
  // Gated on `default` alone. `due_today` / `overdue` are derived server-side
  // FROM a deadline so they cannot reach this branch, and their copy carries an
  // urgency cue that must not vanish even if they somehow did.
  const showDeadlineSegment =
    mission.urgency !== "default" || deadlineText !== ""

  // OKS-480 — `expired` auto-implies the dimmed treatment.
  const isDimmed = dimmed || status === "expired"
  // OKS-480 — show the StatusChip in the top-left when a status is driven and
  // the row is NOT overdue; overdue keeps the UrgencyChip overlay (UX §1/§6).
  //
  // OKS-1002 (D1) — with ONE exception: `to_ship_address_pending` outranks
  // `overdue` and takes the slot. Single-occupancy is unchanged — this is a
  // precedence rule, not a second chip.
  //
  // The rule it encodes: THE SLOT SHOWS THE SIGNAL STATED NOWHERE ELSE ON THE
  // CARD. An overdue row already says so four ways (the EN RETARD section
  // header, this chip, the red AlertTriangle meta line, the destructive
  // colour); address-pending was stated zero ways, on the very rows that had
  // stalled longest. The swap drops a fourth copy of one signal to add the
  // only copy of another.
  //
  // ⚠️ ENUMERATED, NOT DERIVED — a literal comparison against one status, on
  // purpose (BRIEF §3 D1). Not a helper, not a Set, not a prop from the call
  // site: every one of those lets a second member arrive as the fallout of a
  // predicate someone widened rather than as somebody's deliberate act. A
  // second status outranking urgency must cost an edit HERE.
  //
  // The guard that keeps this honest is the existing "KEEPS the UrgencyChip
  // for an overdue row" arm (`status="accepted"`): it stays green under this
  // literal and reds the moment someone unsuppresses the chip for all statuses.
  //
  // OKS-1002 (D3) — and an ACTIONABLE teaser keeps its chip. `lockedActionable`
  // defaults false, so an ordinary locked teaser is unchanged.
  const useStatusChip =
    Boolean(status) &&
    (!overdue || status === "to_ship_address_pending") &&
    (!locked || lockedActionable)
  // OKS-480 — the canonical in-card CTA renders when `ctaState` is set and the
  // row isn't an approved/locked terminal. It makes the card focused (no
  // whole-card link), like `filledCTA`.
  const showInCardCta = Boolean(ctaState) && !approved && !locked
  // OKS-1002 (F1) — the two affordances a teaser must NEVER regain, actionable
  // or not (BRIEF §3 D3). Named here rather than inlined into the action
  // region's guard so each carries its own `!locked` rather than inheriting one
  // from an enclosing expression that F1 proved can be widened out from under
  // them.
  const showFilledCta = Boolean(filledCTA) && !locked
  const showSecondaryAction = Boolean(secondaryAction) && !locked

  // OKS-477 — a locked teaser is never a link and shows no action affordances.
  // OKS-480 — a focused in-card CTA (`ctaState`) also drops the whole-card link.
  // OKS-1002 (D3) — EXCEPT an actionable teaser, which links through to the
  // only page that can clear the creator's outstanding task.
  //
  // ⚠️ THE CHEVRON IS NOT AUTOMATIC — it needs the action region's own gate to
  // admit it (see that region below, edit 5 of D3). An earlier revision of this
  // comment claimed otherwise; the wording is deliberately not restated, because
  // a false sentence quoted in order to refute it stays greppable and gets found
  // by the next reader who lands on that line alone.
  //
  // The failure is worth keeping even though the claim is not: `linkMode &&
  // showChevron` was real and correct, but the region containing it was gated on
  // a bare `!locked` FURTHER DOWN the file, so it was never evaluated. A comment
  // reasoning forward about a downstream expression cannot see a gate upstream of
  // it — "if reached" was the whole question and the prose answered everything
  // else. PO found it in Chrome (F1, frame `02`), no component arm did; the
  // chevron is asserted by an arm now, not promised here.
  const linkMode = !filledCTA && (!locked || lockedActionable) && !showInCardCta
  const detailHref = href ?? creatorMissionDetailPath(collab.id)

  /** OKS-480 — top-left overlay chip: StatusChip when status-driven & calm
   *  (OKS-1002: or address-pending, which outranks overdue), else the
   *  (legacy / overdue) UrgencyChip. Shared by cover + coverless. */
  const topLeftChip = useStatusChip ? (
    <StatusChip status={status!} />
  ) : (
    <UrgencyChip urgency={mission.urgency} />
  )

  // ─── OKS-938 — reward breakdown, type + format (UX Spec §2/§3/§4) ───
  //
  // The §4 conditional table falls out of the card's EXISTING flags plus one
  // new one (`rewardsEarnedOrLost`). Read the table as: the type chip's
  // visibility rule is exactly the chip cluster's (`!locked`), the format's is
  // exactly the meta row's (`!locked`), and the reward elements additionally
  // hide once the outcome is settled.
  //
  //   variant           base | on-time | engagement | type | format
  //   default browse    show | show    | show       | show | show
  //   locked teaser     hide | hide    | hide       | hide | hide
  //   approved          hide | hide    | hide       | show | show
  //   dimmed/expired    hide | hide    | hide       | show | show
  //   overdue           show | HIDE    | show       | show | show
  //
  // `approved` = already earned, `dimmed`/`expired` = "went unclaimed" copy
  // already states the amounts. Advertising a reward in either case is noise.
  const rewardsEarnedOrLost = approved || isDimmed
  const showRewards = !locked && !rewardsEarnedOrLost

  const typeChip = MISSION_TYPE_CHIPS[mission.type]
  const TypeIcon = typeChip.icon

  /** OKS-938 — mission-type chip, joining the existing top-left cluster so it
   *  costs zero new rows. Display-only; both cluster sites are already
   *  `!locked`-gated, which is exactly the §4 table's rule for this chip. */
  const typeChipNode = (
    <Badge variant={typeChip.variant} data-testid="mission-card-type-chip">
      <TypeIcon aria-hidden="true" />
      {t(typeChip.labelKey)}
    </Badge>
  )

  // Format label: `other` swaps in the brand's free text (UX Spec §3), any
  // other value resolves `missions.formats.*`, null renders nothing. Mirrors
  // the shipped ternary in `mission-detail/requirements-list.tsx`.
  //
  // The free-text branch is split out because it is the ONLY one allowed to
  // truncate (UX Spec §7): the six canonical labels are a closed, short set and
  // a clipped "T.." is indistinguishable from a different format.
  const isFreeTextFormat =
    mission.content_format === "other" && Boolean(mission.content_format_other)
  const formatLabel = mission.content_format
    ? isFreeTextFormat
      ? mission.content_format_other
      : t(`missions.formats.${mission.content_format}`)
    : null

  // Base chips — each suppressed independently at 0; if BOTH are 0 the right
  // group is not rendered at all (no empty flex child, no stray gap).
  const baseXp = rewardAtom(t, "xp", mission.reward_xp, i18n.language)
  const baseCoins = rewardAtom(t, "coins", mission.reward_coins, i18n.language)
  const showBaseChips = showRewards && (baseXp !== null || baseCoins !== null)
  // A card with no deadline, no format and no base chips would otherwise leave
  // an empty flex row behind, spending the parent's `gap-2` on nothing — the
  // same stray-gap rule the right group already follows. Reachable since F2:
  // an Open mission with no deadline and no format set.
  const showValueRow =
    showDeadlineSegment || Boolean(formatLabel) || showBaseChips

  // On-time — ONE line carrying both currencies. They are guaranteed to fire
  // together (both awarders delegate to `Collabs::OnTime.judge`), so two
  // independently-conditional lines would imply they can diverge.
  //
  // Hidden on `overdue`: the deadline has passed, the bonus is already lost,
  // and advertising it is cruel rather than informative.
  const onTimeAmounts = composeRewardAmounts(
    t,
    { xp: mission.reward_xp_on_time, coins: mission.reward_coins_on_time },
    i18n.language,
  )
  // ⚠️ Null deadline flips the bonus from conditional to GUARANTEED —
  // `Collabs::OnTime.judge` returns true immediately when there is no
  // deadline (fail-open), so conditional phrasing there is a lie. The
  // amounts are NOT folded into the base chips: that would be a blended
  // total by another name, and would make this card disagree with the
  // approved-celebration chips that read from the ledger.
  const onTimeLine =
    onTimeAmounts && !overdue
      ? deadlineText
        ? t("missions.card.reward.on_time", {
            amounts: onTimeAmounts,
            date: deadlineText,
          })
        : t("missions.card.reward.on_time_no_deadline", {
            amounts: onTimeAmounts,
          })
      : null

  // Engagement — stated as a condition that resolves LATER, never as money
  // arriving with the base reward. Requires BOTH a positive amount and a
  // positive threshold: a 0 threshold leaves the sentence with no threshold
  // to state, so the line is suppressed rather than reworded (UX Spec §4).
  //
  // The backend additionally emits 0/0 when the brand never opted into the
  // bonus, so an un-configured brand lands on this same suppression path
  // rather than advertising a bonus its sweep will never pay.
  const engagementAmounts = composeRewardAmounts(
    t,
    { xp: mission.reward_xp_engagement },
    i18n.language,
  )
  const engagementLine =
    engagementAmounts && mission.engagement_threshold_views > 0
      ? t("missions.card.reward.engagement", {
          amounts: engagementAmounts,
          views: formatXpNumber(
            mission.engagement_threshold_views,
            i18n.language,
          ),
        })
      : null

  return (
    <Card
      className={cn(
        "relative gap-3 overflow-hidden rounded-xl p-0",
        // OKS-1014 — the focus ring lives on the Card ROOT, keyed to the
        // stretched link's :focus-visible. The link is `inset-0` inside this
        // `overflow-hidden` box, so an outset ring on the link is clipped on
        // all four straight edges; the root's own box-shadow is not clipped
        // by its own overflow. Direct-child `>` so a focused CTA never
        // triggers it. Replaces the approved `ring-1` while focused (D6).
        "has-[>[data-slot=card-link]:focus-visible]:ring-ring has-[>[data-slot=card-link]:focus-visible]:ring-2",
        linkMode &&
          "transition-[transform,opacity] motion-safe:active:scale-[0.99] motion-safe:active:opacity-90",
        locked && "opacity-95",
        // OKS-480 — success tint for an approved/completed collab.
        approved &&
          "bg-emerald-50/40 ring-1 ring-emerald-500/40 dark:bg-emerald-950/20",
        className,
      )}
      data-testid="mission-card"
      data-mission-id={mission.id}
      data-locked={locked}
      data-status={status}
      data-approved={approved || undefined}
      data-dimmed={isDimmed || undefined}
      // OKS-1002 (D3 / OQ-F) — an actionable teaser is NOT aria-disabled: a
      // card you can open must not announce itself as disabled. The other
      // teaser treatments below still key off `locked` and are unaffected.
      aria-disabled={(locked && !lockedActionable) || undefined}
    >
      {/* Stretched whole-card link (list mode). Covers the entire card; the
          ghost / filled actions sit above via z-index. It carries NO ring of
          its own — `data-slot="card-link"` is the hook the Card root's
          focus ring keys on (OKS-1014). */}
      {linkMode && (
        <Link
          href={detailHref}
          aria-label={t("missions.card.open_aria", { title: mission.title })}
          className="absolute inset-0 z-0 rounded-xl focus-visible:outline-none"
          data-slot="card-link"
          data-testid="mission-card-link"
        />
      )}

      {/* OKS-480 — approved success check (top-right). Mutually exclusive with
          the Decline overflow (completed rows carry no decline). */}
      {approved && (
        <div className="absolute top-2 right-2 z-10">
          <span
            className="flex size-7 items-center justify-center rounded-full bg-emerald-600 shadow-sm"
            data-testid="mission-card-approved-check"
            aria-label={t("missions.card.status.approved_check_aria")}
            role="img"
          >
            <CheckCircle2 className="size-4 text-white" aria-hidden="true" />
          </span>
        </div>
      )}

      {/* Overflow control (e.g. Decline) — top-right, above the stretched link. */}
      {overflow && !approved && (
        <div
          className="absolute top-2 right-2 z-10"
          data-testid="mission-card-overflow"
        >
          {overflow}
        </div>
      )}

      {/* Cover slot (3:2, OKS-697) with overlaid chips. Collapses when no cover.
          OKS-452 — `onLoad` flips `coverLoaded`, fading the image in from
          opacity-0 over ~120ms via `motion-safe:` (reduced-motion users see
          the image at full opacity immediately).

          OKS-719 — `pointer-events-none` is LOAD-BEARING: it completes the
          OKS-464 stretched-link contract. This container is `relative`
          (positioned) and a later DOM sibling of the `z-0` link above, so it
          paints over the link and — without this — swallowed every cover tap,
          leaving the largest, most tappable-looking part of the card dead.
          Declining pointer events lets taps fall through to the link beneath.

          Deliberate properties of this shape, do not "simplify":
            - It fixes hit-testing WITHOUT touching paint order, which is what
              makes the correction pixel-identical to the pre-fix render.
            - `relative` + `overflow-hidden` MUST stay: `relative` is the
              positioning context for the scrim/chips/social proof (dropping it
              would reposition them against the Card root), `overflow-hidden`
              clips the img to the aspect box.
            - `pointer-events` inherits, so this covers the img + chips + social
              proof — all display-only today. Anything added here that needs to
              be interactive must opt back in with `pointer-events-auto`.
            - Unconditional, not gated on `linkMode`: locked / filled-CTA cards
              render no link beneath, so a cover tap still resolves to nothing —
              identical to today. The cover is a passive surface on every
              variant; it should never intercept, only pass through. */}
      {showCover && (
        <div
          className={cn(
            COVER_ASPECT_CLASS,
            "pointer-events-none relative w-full overflow-hidden",
          )}
          data-testid="mission-card-cover"
        >
          <img
            src={mission.cover_image_url ?? ""}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setCoverErrored(true)}
            onLoad={() => setCoverLoaded(true)}
            data-testid="mission-card-cover-img"
            className={cn(
              "h-full w-full object-cover",
              coverLoaded ? "opacity-100" : "opacity-0",
              "motion-safe:transition-opacity motion-safe:duration-[120ms]",
              locked && "brightness-50 saturate-75",
              // OKS-480 — expired/dimmed is a LIGHTER mute than the locked
              // teaser; the chip + CTA stay legible above the scrim.
              isDimmed && !locked && "brightness-90 saturate-50",
            )}
          />
          {/* OKS-477 — locked teaser scrim + centered lock badge. */}
          {locked && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-black/40"
              data-testid="mission-card-lock-overlay"
            >
              <span className="flex size-12 items-center justify-center rounded-full bg-white/90 shadow-sm">
                <Lock className="size-5 text-neutral-800" aria-hidden="true" />
              </span>
            </div>
          )}
          {/* OKS-480 — expired/dimmed scrim (lighter than locked; chip stays
              above it via z-index). */}
          {isDimmed && !locked && (
            <div
              className="bg-background/30 pointer-events-none absolute inset-0"
              data-testid="mission-card-dimmed"
            />
          )}
          {/* OKS-1002 (D3) — an actionable teaser shows the chip cluster, but
              the TYPE CHIP stays withheld: the lock still hides what the
              mission IS. Only the creator's own outstanding task comes back.
              ⚠️ The coverless fallback below is a SECOND copy of this gate and
              must move with it. */}
          {(!locked || lockedActionable) && (
            <div className="pointer-events-none absolute top-2 left-2 z-[1] flex flex-wrap items-center gap-1">
              {topLeftChip}
              {!locked && typeChipNode}
            </div>
          )}
          {!locked && mission.social_proof && (
            <div className="absolute bottom-2 left-2 z-[1]">
              <SocialProofCluster socialProof={mission.social_proof} />
            </div>
          )}
        </div>
      )}

      <CardContent className="flex flex-col gap-2 p-4">
        {/* Coverless fallback — chip + social proof inline (UX Spec §3 edge).
            Suppressed when locked (no urgency cue on a teaser; the coverless
            lock badge renders below the title via the locked caption). */}
        {/* OKS-1002 (D3) — the second copy of the cover cluster's gate. */}
        {!showCover && (!locked || lockedActionable) && (
          <div className="flex flex-wrap items-center gap-2">
            {topLeftChip}
            {!locked && typeChipNode}
            {mission.social_proof && (
              <SocialProofCluster
                socialProof={mission.social_proof}
                className="bg-muted text-muted-foreground"
              />
            )}
          </div>
        )}

        {/* Title row — title (clamp-2) + OKS-637 "Action required" badge
            trailing. flex-wrap so the badge drops to its own line on narrow
            viewports rather than crushing the title (UX Spec §5). Never on a
            locked teaser (locked collabs are never ready-to-submit). */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3
            className="line-clamp-2 min-w-0 flex-1 text-base font-semibold"
            data-testid="mission-card-title"
          >
            {mission.title}
          </h3>
          {actionRequired && !locked && <ActionRequiredBadge />}
        </div>

        {/* OKS-986 — the gift row, directly below the title and above the
            value row: the title stays the scan anchor and the gift reads as
            the first item of the "what I get" stack (UX D2.1).

            ⚠️ NO `locked` guard here, deliberately. The server omits
            `collab.product` entirely while a drop is pending, so a locked
            teaser has nothing to render — the suppression is a disclosure rule
            enforced on the wire, not a display rule duplicated here. Adding a
            `!locked` check would be a second, driftable copy of a gate that
            already holds. See `Collabs::CardSerializer#gift_visible?`.

            ⚠️ Static flow content — never positioned. It is a SIBLING of the
            `z-0` stretched link, so `relative`/`absolute` would paint it over
            the link and swallow taps (UX D9). */}
        <GiftLine
          product={collab.product}
          offer={collab.offer}
          missionType={mission.type}
          locale={locale}
        />

        {/* OKS-988 — "you already have this" told to the CREATOR, directly
            under the gift row and ABOVE the meta/reward row (UX §S1 order).

            ⚠️ A SIBLING of GiftLine, never inside it. That row's three
            truncation classes are watched by a measured-geometry E2E arm and
            broke invisibly twice during OKS-986; adding a node inside it is
            exactly what the arm exists to catch. Also static flow content, for
            the same stretched-link reason as GiftLine above. */}
        {!locked && (
          <RecencyLine
            entry={collab.entry}
            brandName={collab.brand.name}
            // ⚠️ 0 ON PURPOSE, and it is a spec decision not an oversight.
            // OKS-990 decision 4's clause is a DETAIL-surface sentence (UX §S2
            // "in the page body", anchor `mission-detail-recency-multi-clause`).
            // The card has one compact line; a two-line consequence clause there
            // would push the gift row and the CTA below the fold at 402px. Passed
            // explicitly rather than omitted so the omission reads as a choice.
            offerCount={0}
          />
        )}

        {/* OKS-477 — locked teaser caption (replaces the deadline meta line):
            lock icon + "Locked until drop", never colour-only (a11y). */}
        {locked ? (
          <div
            className="text-muted-foreground flex items-center gap-1.5 text-sm"
            data-testid="mission-card-locked-caption"
          >
            <Lock className="size-4 shrink-0" aria-hidden="true" />
            <span>{t("missions.card.locked")}</span>
          </div>
        ) : showValueRow ? (
          /* OKS-938 — VALUE ROW (was the deadline meta row).
             Left: deadline (keeps its urgency colour) + content format.
             Right: the base XP / coins chips.

             This is what buys the density budget — three of the six additions
             land here rather than on rows of their own.

             ⚠️ The left group is `flex-auto`, NOT `flex-1`. This is the whole
             of the OKS-938 F1 fix and it is not a style preference. `flex-1`
             is `flex: 1 1 0%`, so the group's HYPOTHETICAL main size — the
             width flexbox uses to decide where to break a line — is 0. It
             therefore never triggers a wrap: the chips stayed pinned to line 1
             at every viewport, the left group absorbed all the shrink, and
             `truncate` ate the format ("Tutoriel" → "T.." at 375px, gone
             entirely at 320px). `flex-auto` is `flex: 1 1 auto`, whose base
             size is the content width, so once deadline + format + chips
             exceed the row the CHIPS wrap to their own line and the left group
             keeps its full width. `flex-grow: 1` is retained, so while they do
             share a line the chips still sit hard right, exactly as before.

             The left group wraps internally too, which is what makes "canonical
             labels never clip" structural rather than measured: the format can
             always fall to a line of its own instead of being squeezed.

             ⚠️ KD-8: no `relative`, no `z-*` anywhere in this row. The
             whole-card link is `absolute inset-0 z-0`, and a positioned
             element with z-index 0/auto paints ABOVE static in-flow content —
             that is precisely how the cover swallowed every tap before
             OKS-719. Static content falls through to the link beneath, which
             is what we want for display-only rows. */
          <div
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm"
            data-testid="mission-card-value-row"
          >
            {(showDeadlineSegment || formatLabel) && (
              <div
                className={cn(
                  "flex min-w-0 flex-auto flex-wrap items-center gap-x-1.5 gap-y-0.5",
                  meta.textClass,
                )}
                data-testid="mission-card-meta"
              >
                {/* Icon and text ride in one non-wrapping unit so the calendar
                    glyph can never strand itself at the end of a line. */}
                {showDeadlineSegment && (
                  <span className="flex items-center gap-1.5">
                    <MetaIcon className="size-4 shrink-0" aria-hidden="true" />
                    {metaText}
                  </span>
                )}
                {formatLabel && (
                  /* The separator travels WITH the format rather than sitting
                     between the two groups: on the wrap it would otherwise
                     dangle at the end of the deadline line pointing at
                     nothing. */
                  <span className="flex min-w-0 items-center gap-1.5">
                    {showDeadlineSegment && <span aria-hidden="true">·</span>}
                    {/* Explicitly muted so it never inherits the destructive /
                        amber urgency tone from the deadline beside it.

                        ⚠️ `truncate` is applied to the free-text branch ONLY.
                        The six canonical labels are a closed set of short words
                        and MUST render whole at ≥320px in both locales (UX Spec
                        §7) — a clipped "T.." is unreadable as "Tutoriel" and
                        reads as data loss. They get `nowrap` and, if the line
                        is tight, the whole group wraps instead of clipping.

                        Free text still truncates — it is a label, not a
                        sentence — but never below ~12 characters: this group
                        can always take a full row of its own, which is ~36ch at
                        320px, so the ellipsis only ever appears well past that
                        floor. That floor is structural, not a `min-width`; a
                        `min-w-[12ch]` would also pad SHORT free text out to
                        12ch and force needless wraps. */}
                    <span
                      className={cn(
                        "text-muted-foreground",
                        isFreeTextFormat ? "truncate" : "whitespace-nowrap",
                      )}
                      data-testid="mission-card-format"
                    >
                      {formatLabel}
                    </span>
                  </span>
                )}
              </div>
            )}

            {/* Right group omitted ENTIRELY when both amounts are 0 — no empty
                flex child, no stray gap (UX Spec §4). */}
            {showBaseChips && (
              <div className="flex shrink-0 items-center gap-1">
                {baseXp && (
                  <Badge
                    variant="secondary"
                    data-testid="mission-card-reward-xp"
                  >
                    <Sparkles aria-hidden="true" />
                    {baseXp}
                  </Badge>
                )}
                {baseCoins && (
                  <Badge
                    variant="secondary"
                    data-testid="mission-card-reward-coins"
                  >
                    <Coins aria-hidden="true" />
                    {baseCoins}
                  </Badge>
                )}
              </div>
            )}
          </div>
        ) : null}

        {/* OKS-938 — ON-TIME bonus line. One line, both currencies (they are
            guaranteed to fire together). Wraps but never truncates: cutting
            "…if you post by" mid-condition would turn a conditional into an
            unconditional claim. Static — see KD-8 note above. */}
        {showRewards && onTimeLine && (
          <div
            className="text-muted-foreground flex items-start gap-1.5 text-xs"
            data-testid="mission-card-bonus-on-time"
          >
            <Clock className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>{onTimeLine}</span>
          </div>
        )}

        {/* OKS-938 — ENGAGEMENT bonus line. Reads as conditional AND later;
            never summed into the base figure. */}
        {showRewards && engagementLine && (
          <div
            className="text-muted-foreground flex items-start gap-1.5 text-xs"
            data-testid="mission-card-bonus-engagement"
          >
            <TrendingUp
              className="mt-0.5 size-3.5 shrink-0"
              aria-hidden="true"
            />
            <span>{engagementLine}</span>
          </div>
        )}

        {/* Action region.
            OKS-477 — suppressed on a locked teaser.
            OKS-1002 (D3, F1) — EXCEPT for the chevron on an actionable teaser.

            ⚠️ THE FIFTH GATE. `lockedActionable` widens four other clauses
            (`useStatusChip`, `linkMode`, `aria-disabled`, both chip clusters);
            this outer `locked` guard was missed, so the card was a real link —
            keyboard-reachable, accessibly named — that looked identical to the
            non-tappable teaser beside it. PO caught it in Chrome (frame `02`);
            no component arm did, because none asserted the chevron on a teaser.

            ⚠️ `showFilledCta` / `showSecondaryAction` stay `!locked` and are
            NOT widened, and that is measured rather than assumed: widening only
            this outer guard re-admits BOTH on a locked row (probed — each
            renders), which D3 forbids outright ("no rewards, no picker, no
            decline overflow, no secondary action"). The call site happens to
            pass neither on a locked row, so the naive fix would have looked
            correct from `/missions` while leaving the component wrong for any
            future caller. One expression per affordance, each stating its own
            rule. */}
        {(!locked || lockedActionable) &&
          (showFilledCta ||
            showSecondaryAction ||
            showInCardCta ||
            (linkMode && showChevron)) && (
            <div className="mt-1 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {showSecondaryAction &&
                  secondaryAction &&
                  (secondaryAction.onClick ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="relative z-10 min-h-11"
                      data-testid="mission-card-secondary-action"
                      onClick={secondaryAction.onClick}
                    >
                      {t(secondaryAction.labelKey)}
                    </Button>
                  ) : (
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="relative z-10 min-h-11"
                      data-testid="mission-card-secondary-action"
                    >
                      <Link href={secondaryAction.href ?? "#"}>
                        {t(secondaryAction.labelKey)}
                      </Link>
                    </Button>
                  ))}
                {showFilledCta && filledCTA && (
                  <Button
                    asChild
                    size="sm"
                    className="relative z-10 min-h-11"
                    data-testid="mission-card-filled-cta"
                  >
                    <Link href={filledCTA.href}>{t(filledCTA.labelKey)}</Link>
                  </Button>
                )}
                {showInCardCta && ctaState && (
                  <MissionCardCta
                    state={ctaState}
                    label={ctaLabel ? t(ctaLabel) : ""}
                    action={ctaAction}
                  />
                )}
              </div>
              {linkMode && showChevron && (
                <ChevronRight
                  className="text-muted-foreground size-5 shrink-0"
                  aria-hidden="true"
                  data-testid="mission-card-chevron"
                />
              )}
            </div>
          )}
      </CardContent>
    </Card>
  )
}

/** OKS-480 — canonical `ctaState` → shadcn Button variant map. */
const CTA_VARIANT: Record<
  MissionCardCtaState,
  "default" | "outline" | "destructive"
> = {
  enabled: "default",
  "disabled-progress": "outline",
  destructive: "destructive",
  outline: "outline",
}

/**
 * OKS-480 — the canonical in-card CTA. `disabled-progress` renders a muted,
 * non-interactive affordance (focusable + readable, no handler) matching the
 * shipped `CTAButton` DISABLED_KINDS semantics; the other states are
 * actionable (href via Inertia `Link`, or an `onClick`). 44px tap target.
 */
function MissionCardCta({
  state,
  label,
  action,
}: {
  state: MissionCardCtaState
  label: string
  action?: MissionCardCtaAction
}) {
  const disabled = state === "disabled-progress"
  const variant = CTA_VARIANT[state]
  const className = "relative z-10 min-h-11"

  if (disabled || (!action?.href && !action?.onClick)) {
    return (
      <Button
        type="button"
        variant={variant}
        disabled={disabled}
        className={className}
        data-testid="mission-card-cta"
        data-cta-state={state}
      >
        {label}
      </Button>
    )
  }

  if (action.onClick) {
    return (
      <Button
        type="button"
        variant={variant}
        className={className}
        data-testid="mission-card-cta"
        data-cta-state={state}
        onClick={action.onClick}
      >
        {label}
      </Button>
    )
  }

  return (
    <Button
      asChild
      variant={variant}
      className={className}
      data-testid="mission-card-cta"
      data-cta-state={state}
    >
      <Link href={action.href ?? "#"}>{label}</Link>
    </Button>
  )
}
